import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { ShippingActions } from './ShippingActions';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

export function BookingDetailsModal({ booking, onClose, onUpdate }) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteOrphanedBooking = async () => {
    if (!window.confirm('Diese Buchung hat keine zugeordnete Bestellung und kann gelöscht werden. Möchtest du fortfahren?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.delete(`/admin/bookings/${bookingId}`);

      if (response.success) {
        toast.success('Buchung erfolgreich gelöscht');
        onUpdate();
        onClose();
      } else {
        toast.error(response.error || 'Fehler beim Löschen der Buchung');
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Fehler beim Löschen der Buchung');
    } finally {
      setIsDeleting(false);
    }
  };

  const getShippingStatusBadge = (status) => {
    const variants = {
      not_shipped: 'warning',
      preparing: 'info',
      shipped: 'success',
      delivered: 'success',
      returned: 'secondary',
      overdue: 'error'
    };

    const labels = {
      not_shipped: 'Nicht versendet',
      preparing: 'Vorbereitung',
      shipped: 'Versendet',
      delivered: 'Zugestellt',
      returned: 'Zurück',
      overdue: 'Überfällig'
    };

    return (
      <Badge variant={variants[status] || 'secondary'} size="sm">
        {labels[status] || status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
  };

  // Support both camelCase and snake_case field names
  const bookingId = booking.bookingId || booking.booking_id;
  const customerName = booking.customerName || booking.customer_name;
  const customerEmail = booking.customerEmail || booking.customer_email;
  const productTitle = booking.productTitle || booking.product_title;
  const variantTitle = booking.variantTitle || booking.variant_title;
  const startDate = booking.startDate || booking.start_date || booking.eventDate || booking.event_date;
  const endDate = booking.endDate || booking.end_date;
  const totalDays = booking.totalDays || booking.total_days;
  const status = booking.status;
  const shippingStatus = booking.shippingStatus || booking.shipping_status || 'not_shipped';
  const orderId = booking.orderId || booking.order_id;

  // Debug log
  console.log('[BookingDetailsModal] Booking data:', {
    bookingId,
    orderId,
    rawOrderId: booking.orderId,
    rawOrder_id: booking.order_id,
    fullBooking: booking
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Buchungsdetails</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Booking Details */}
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-500">Kunde:</span>
            <p className="text-gray-900">{customerName || 'Unbekannt'}</p>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Email:</span>
            <p className="text-gray-900">{customerEmail || '-'}</p>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Produkt:</span>
            <p className="text-gray-900">
              {productTitle || 'Fotobox'}
              {variantTitle && ` - ${variantTitle}`}
            </p>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Zeitraum:</span>
            <p className="text-gray-900">
              {formatDate(startDate)}
              {endDate && endDate !== startDate && (
                <> - {formatDate(endDate)}</>
              )}
              {totalDays > 1 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({totalDays} Tage)
                </span>
              )}
            </p>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Buchungsstatus:</span>
            <div className="mt-1">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                status === 'confirmed' ? 'bg-green-100 text-green-800' :
                status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {status === 'confirmed' ? 'Bestätigt' :
                 status === 'pending' ? 'Ausstehend' :
                 status}
              </span>
            </div>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Versandstatus:</span>
            <div className="mt-1">
              {getShippingStatusBadge(shippingStatus)}
            </div>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Buchungs-ID:</span>
            <p className="text-gray-900 text-xs font-mono break-all">{bookingId}</p>
          </div>

          {/* Event Code Display */}
          <div>
            <span className="text-sm font-medium text-gray-500">Event-Code:</span>
            <div className="mt-1 flex items-center gap-2">
              <p className="text-gray-900 text-2xl font-bold font-mono tracking-wider">
                {booking.eventCode || booking.event_code || '-'}
              </p>
              {(booking.eventCode || booking.event_code) && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(booking.eventCode || booking.event_code);
                    // Simple toast notification
                    const toast = document.createElement('div');
                    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded shadow-lg z-50';
                    toast.textContent = 'Event-Code kopiert!';
                    document.body.appendChild(toast);
                    setTimeout(() => toast.remove(), 2000);
                  }}
                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                  title="Code kopieren"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              )}
            </div>
            {(booking.eventCodeExpiresAt || booking.event_code_expires_at) && (
              <p className="text-xs text-gray-500 mt-1">
                Gültig bis: {new Date(booking.eventCodeExpiresAt || booking.event_code_expires_at).toLocaleDateString('de-DE', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
                {new Date(booking.eventCodeExpiresAt || booking.event_code_expires_at) < new Date() && (
                  <span className="ml-2 text-red-600 font-medium">(Abgelaufen)</span>
                )}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Für Fotobox App und Diashow-Zugriff
            </p>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-500">Bestellung:</span>
            <div className="mt-1">
              {orderId ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onClose();
                      navigate(`/orders?orderId=${encodeURIComponent(orderId)}`);
                    }}
                    className="text-blue-600 hover:text-blue-800 p-0 h-auto font-normal"
                  >
                    <span className="flex items-center gap-1">
                      Zur Bestellung ({orderId.split('/').pop()})
                      <ExternalLink className="w-3 h-3" />
                    </span>
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">
                    Um diese Buchung zu stornieren, muss die Bestellung rückerstattet werden.
                  </p>
                </>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Keine Bestellung verknüpft (Ghost Booking)
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Diese Buchung kann direkt gelöscht werden.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Shipping Actions Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Versand & Tracking</h4>
          <ShippingActions
            booking={booking}
            onUpdate={() => {
              if (onUpdate) onUpdate();
              onClose();
            }}
          />
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between items-center gap-3">
          {/* Delete button for orphaned bookings (no order_id) */}
          {!orderId && (
            <Button
              onClick={handleDeleteOrphanedBooking}
              variant="ghost"
              disabled={isDeleting}
              className="text-red-600 hover:text-red-800 hover:bg-red-50"
            >
              {isDeleting ? 'Löschen...' : 'Buchung löschen'}
            </Button>
          )}

          <div className="ml-auto">
            <Button
              onClick={onClose}
              variant="secondary"
            >
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingDetailsModal;
